import { cfetch } from '@confluence/network';
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { fg } from '@confluence/feature-gating';
import { isLocal } from '@confluence/ssr-utilities/entry-points/env.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';

import { LocalStorageQuery } from './LocalStorageQueryRaw.ts';
import { getKeysForLocalStorageQuery } from './storageHelpers.ts';
import type { LocalStorageQueryVariables } from './__types__/LocalStorageQuery.ts';
import type { StorageManagerInitContext } from './StorageManagerInitContext.ts';

export async function getRemoteLocalStorage({
	userId,
	isLicensed,
	regionSteeringHint,
}: StorageManagerInitContext): Promise<any> {
	if (window['__REMOTE_LOCAL_STORAGE__']) {
		return window['__REMOTE_LOCAL_STORAGE__'];
	}

	const tenantHostname =
		typeof window !== 'undefined' ? window.location?.hostname : getSSRRenderInputs().host;

	const regionHint =
		fg('cc-agg-migration-use-agg-endpoint') || isGCPtenant(tenantHostname)
			? regionSteeringHint || window['GLOBAL_AGG_REGION_STEERING_HINT']
			: undefined;

	const URI =
		fg('cc-agg-migration-use-agg-endpoint') || isGCPtenant(tenantHostname)
			? '/gateway/api/graphql/slow'
			: window['GLOBAL_APOLLO_CLIENT_URI'] || '/cgraphql';
	return (window['__REMOTE_LOCAL_STORAGE__'] = SSRMeasures.run(
		'ssr-app/prepare/preloadQuery/fetch:LocalStorageQuery',
		async () => {
			let result = null;
			try {
				if (isLicensed && userId) {
					const variables: LocalStorageQueryVariables = {
						stringKeys: getKeysForLocalStorageQuery(userId),
					};

					const res = await cfetch(`${URI}?q=LocalStorageQuery`, {
						method: 'POST',
						credentials: 'include',
						headers: {
							'X-APOLLO-OPERATION-NAME': 'LocalStorageQuery',
							'Content-Type': 'application/json',
							// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
							...(Boolean(process.env.REACT_SSR) &&
							Boolean(process.env.BIFROST_TEMPLATE_BUILD) &&
							!isLocal()
								? { 'x-ssr-asap-audience': 'cc-graphql' }
								: {}),
							...(regionHint ? { 'Atl-Edge-Region-Hint': regionHint } : {}),
						},
						body: JSON.stringify({
							operationName: 'LocalStorageQuery',
							query: LocalStorageQuery,
							variables,
						}),
					});
					if (res.ok) {
						const { data } = await res.json();
						result = data?.localStorage?.stringValues;
					}
				}
			} catch {}
			return (window['__REMOTE_LOCAL_STORAGE__'] = result);
		},
	));
}

/**
 * Checks if the current domain is a GCP tenant matching the pattern: .*-cdp-{cdp-id}\.jira-dev\.com
 * @param {string | undefined} hostname optional hostname to evaluate
 * @returns {boolean} true if the domain matches the GCP tenant pattern
 */
function isGCPtenant(hostname?: string): boolean {
	if (!hostname) {
		return false;
	}

	const gcpTenantPattern = /^.*-cdp-\w+\.jira-dev\.com$/;
	return gcpTenantPattern.test(hostname);
}
